/**
 * Theme utilities — spec 046 D-DarkMode-01.
 *
 * - `applyTheme(theme)` ajoute/retire la classe `.dark` sur `<html>` selon le mode souhaité.
 *   Si `theme === 'auto'`, suit `prefers-color-scheme: dark`.
 * - `getInitialTheme()` retourne le thème en cache localStorage pour éviter le flash au boot.
 * - `getEffectiveTheme(theme)` résout le thème effectif (`light` ou `dark`) selon la préférence.
 */

export type Theme = 'auto' | 'light' | 'dark'
export type EffectiveTheme = 'light' | 'dark'

const STORAGE_KEY = 'theme-cache'
const THEME_EVENT = 'pragmelis:theme-changed'

/**
 * Notifie toutes les instances de `useTheme` qu'un changement a eu lieu — via un
 * CustomEvent DOM, donc indépendant de la hiérarchie React. Permet que le sélecteur
 * du Header et celui de la page Paramètres restent synchronisés sans Context partagé.
 */
export function notifyThemeChanged(theme: Theme): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent<Theme>(THEME_EVENT, { detail: theme }))
}

export function onThemeChanged(handler: (theme: Theme) => void): () => void {
  if (typeof window === 'undefined') return () => {}
  const listener = (e: Event) => {
    const detail = (e as CustomEvent<Theme>).detail
    if (detail === 'auto' || detail === 'light' || detail === 'dark') {
      handler(detail)
    }
  }
  window.addEventListener(THEME_EVENT, listener)
  return () => window.removeEventListener(THEME_EVENT, listener)
}

export function getInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'auto'
  try {
    const cached = window.localStorage.getItem(STORAGE_KEY)
    if (cached === 'auto' || cached === 'light' || cached === 'dark') return cached
  } catch {
    // ignore
  }
  return 'auto'
}

export function setCachedTheme(theme: Theme): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, theme)
  } catch {
    // ignore
  }
}

function prefersDark(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

export function getEffectiveTheme(theme: Theme): EffectiveTheme {
  if (theme === 'dark') return 'dark'
  if (theme === 'light') return 'light'
  // auto
  return prefersDark() ? 'dark' : 'light'
}

/**
 * Applique le thème en ajoutant/retirant la classe `.dark` sur `<html>`.
 * Met à jour le cache localStorage pour éviter le flash au prochain chargement.
 */
export function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return
  const effective = getEffectiveTheme(theme)
  const root = document.documentElement
  if (effective === 'dark') {
    root.classList.add('dark')
  } else {
    root.classList.remove('dark')
  }
  setCachedTheme(theme)
}
